import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Megaphone, X } from '../../../icons';
import { getRoomNotice } from '../../../api/chat-notice';
import { useChatNoticeStore, shouldShowNotice } from '../../../store/chatNoticeStore';
import './ChatNoticeBanner.css';

/**
 * 방 공지 띠 (room-notice — 대화 위 고정 한 줄).
 *
 * 서버·앱은 이미 이 기능을 쓰고 있었고 PC 만 없었다. 그래서 새로 만드는 것은 화면뿐이다.
 *
 * 동작:
 *   - 방을 열면 공지를 한 번 불러온다. 없으면 아무것도 그리지 않는다(높이 0 — 기존 레이아웃 무영향).
 *   - 길면 한 줄로 줄여 보여 주고 '전체보기' 로 펼친다.
 *   - X 로 접으면 **그 본문**을 접은 것으로 기억한다. 공지가 새 내용으로 바뀌면 다시 뜬다.
 *
 * 공지를 불러오는 책임을 이 컴포넌트가 지므로 ChatArea 는 한 줄만 더한다(additive).
 */
export function ChatNoticeBanner({ roomId }: { roomId: string }) {
  const notice = useChatNoticeStore((s) => s.noticeByRoom[roomId]);
  const collapsedText = useChatNoticeStore((s) => s.collapsedByRoom[roomId]);
  const setNotice = useChatNoticeStore((s) => s.setNotice);
  const collapse = useChatNoticeStore((s) => s.collapse);
  const [expanded, setExpanded] = useState(false);

  // 방을 바꾸면 펼침 상태는 초기화한다 — 다른 방의 공지를 펼친 채로 들어오지 않게.
  useEffect(() => {
    setExpanded(false);
  }, [roomId]);

  useEffect(() => {
    let cancelled = false;
    getRoomNotice(roomId)
      .then((n) => {
        if (!cancelled) setNotice(roomId, n);
      })
      .catch(() => {
        // 조회 실패(권한·네트워크)는 조용히 넘어간다 — 공지는 대화를 막을 만한 것이 아니다.
        if (!cancelled) setNotice(roomId, null);
      });
    return () => {
      cancelled = true;
    };
  }, [roomId, setNotice]);

  if (!shouldShowNotice(notice, collapsedText)) return null;

  const multiline = notice.text.length > 40 || notice.text.includes('\n');

  return (
    <div className={`chat-notice${expanded ? ' is-open' : ''}`} role="region" aria-label="방 공지">
      <span className="chat-notice-ico" aria-hidden>
        <Megaphone size={14} />
      </span>
      <span className={`chat-notice-text${expanded ? '' : ' is-clamped'}`}>{notice.text}</span>
      {multiline && (
        <button
          type="button"
          className="chat-notice-more"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? '접기' : '전체보기'}
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      )}
      <button
        type="button"
        className="chat-notice-close"
        onClick={() => collapse(roomId, notice.text)}
        aria-label="공지 접기"
        title="공지 접기 — 새 공지가 등록되면 다시 보입니다"
      >
        <X size={13} />
      </button>
    </div>
  );
}
